/**
 * Research domain: gebieden (lands) → monsters (samples) →
 * onderzoeken (microscopy research) → adviezen (advice).
 *
 * Shared by the Worker and the SPA, so DOM-free. Request bodies are zod
 * schemas; responses are plain interfaces. Timestamps are epoch ms.
 */
import { z } from "zod";
import type { TargetSources, Targets } from "./targets.ts";

export const LAND_USES = [
  "arable",
  "vegetables",
  "grassland",
  "bulbs",
  "orchard",
  "berries",
  "vineyard",
  "forest",
] as const;
export type LandUse = (typeof LAND_USES)[number];

export const SOIL_TYPES = ["sand", "clay", "peat", "loam", "loess"] as const;
export type SoilType = (typeof SOIL_TYPES)[number];

export const MANAGEMENT_TYPES = ["conventional", "transition", "organic", "regenerative"] as const;
export type ManagementType = (typeof MANAGEMENT_TYPES)[number];

/**
 * draft (owner edits) → sent (owner posted it) → received (lab) → analyzing
 * (research draft) → analyzed (research completed).
 */
export const SAMPLE_STATUSES = ["draft", "sent", "received", "analyzing", "analyzed"] as const;
export type SampleStatus = (typeof SAMPLE_STATUSES)[number];

export const MOISTURE_STATES = ["dry", "moist", "wet"] as const;
export type MoistureState = (typeof MOISTURE_STATES)[number];

/** Owners only ever see completed research. */
export const RESEARCH_STATUSES = ["draft", "completed"] as const;
export type ResearchStatus = (typeof RESEARCH_STATUSES)[number];

/** Owners only ever see published advice. */
export const ADVICE_STATUSES = ["draft", "published"] as const;
export type AdviceStatus = (typeof ADVICE_STATUSES)[number];

/**
 * fungal = beneficial fungi; oomycete = water moulds (not beneficial);
 * actinomycete = thin bacterial filaments.
 */
export const HYPHA_KINDS = ["fungal", "oomycete", "actinomycete"] as const;
export type HyphaKind = (typeof HYPHA_KINDS)[number];

export const BACTERIA_CLASSES = [
  "bacterium",
  "coccus",
  "rod",
  "long_rod",
  "vibrio",
  "spirillum",
  "spirochete",
] as const;
export type BacteriaClass = (typeof BACTERIA_CLASSES)[number];
export type BacteriaClassCounts = Record<BacteriaClass, number>;

export const emptyBacteriaClassCounts = (): BacteriaClassCounts => ({
  bacterium: 0,
  coccus: 0,
  rod: 0,
  long_rod: 0,
  vibrio: 0,
  spirillum: 0,
  spirochete: 0,
});

/** Null marks an active organism. */
export const ORGANISM_STATES = ["cyst", "inactive"] as const;
export type OrganismState = (typeof ORGANISM_STATES)[number];

/** The course's nine hypha colours, confirmed by the analyst. */
export const HYPHA_COLORS = [
  "clear",
  "yellow",
  "orange",
  "red",
  "green",
  "purple",
  "tan",
  "brown",
  "dark_brown",
] as const;
export type HyphaColor = (typeof HYPHA_COLORS)[number];
type LegacyHyphaColor = "brown" | "amber" | "colorless";

export const upgradeHyphaColor = (color: LegacyHyphaColor | HyphaColor): HyphaColor =>
  color === "amber" ? "tan" : color === "colorless" ? "clear" : color;

/** AnalysisInput count keys, one per nematode feeding group. */
export const NEMATODE_GROUPS = [
  "bacterialFeeders",
  "fungalFeeders",
  "predatory",
  "rootFeeders",
  "switchers",
  "bacterialOmnivores",
  "fungalOmnivores",
] as const;
export type NematodeGroup = (typeof NEMATODE_GROUPS)[number];

export const MEASURE_CATEGORIES = [
  "compost",
  "compost_tea",
  "cover_crop",
  "tillage",
  "water_air",
  "organic_matter",
  "inputs",
  "grazing",
  "monitoring",
] as const;
export type MeasureCategory = (typeof MEASURE_CATEGORIES)[number];

export const PRIORITIES = ["high", "medium", "low"] as const;
export type Priority = (typeof PRIORITIES)[number];

/** Overall soil-life grade from the 0–100 score; bounds in domain/scoring.ts (GRADE_BOUNDS). */
export const GRADES = ["good", "fair", "moderate", "poor"] as const;
export type Grade = (typeof GRADES)[number];

/** Indicators scored against the land use's targets, in display (radar) order. */
export const INDICATOR_KEYS = [
  "bacteria",
  "fungi",
  "fbRatio",
  "flagellates",
  "amoebae",
  "beneficialNematodes",
  "ciliates",
  "rootFeeders",
] as const;
export type IndicatorKey = (typeof INDICATOR_KEYS)[number];

/**
 * missing = not measured (no fields/drops examined or no dilution for that group);
 * unscored = measured, but the counting effort cannot judge it against its target.
 */
export type IndicatorStatus = "low" | "ok" | "high" | "missing" | "unscored";

/**
 * low_effort = fewer fields/drops than the minimum effort;
 * below_detection = the organisms counted cannot resolve the target (see Indicator.detectionLimit).
 */
export type UnscoredReason = "low_effort" | "below_detection";

/** share = fraction (0–1) of the organisms counted in the group. */
export type IndicatorUnit = "ugPerG" | "perG" | "ratio" | "share";

/** direct = microscopy counts; mpn = most-probable-number culture entered by the analyst. */
export type IndicatorSource = "direct" | "mpn";

/** Rule-based advice suggestions; the SPA maps each key to localized title/description/timing. */
export type SuggestionKey =
  | "raise_fungi"
  | "reduce_tillage"
  | "living_roots"
  | "raise_bacteria"
  | "lower_fb"
  | "add_protozoa"
  | "add_nematodes"
  | "improve_aeration"
  | "root_feeders"
  | "oomycetes"
  | "complete_analysis"
  | "keep_going"
  | "retest";

// ---------------------------------------------------------------------------
// Lands (gebieden)
// ---------------------------------------------------------------------------

export const landInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  /** Place, address or parcel reference. */
  location: z.string().trim().max(200),
  areaHa: z.number().positive().max(100_000).nullable(),
  landUse: z.enum(LAND_USES),
  soilType: z.enum(SOIL_TYPES),
  management: z.enum(MANAGEMENT_TYPES),
  notes: z.string().trim().max(4000),
});
export type LandInput = z.infer<typeof landInputSchema>;

/**
 * Creating a land may name its owner (analysts pick one; owners only themselves).
 * Updates use `LandInput`: the owner never changes.
 */
export const createLandInputSchema = landInputSchema.extend({
  ownerId: z.string().min(1).optional(),
});
export type CreateLandInput = z.infer<typeof createLandInputSchema>;

/** A user account as analysts pick it, e.g. as a land's owner. */
export interface UserSummary {
  id: string;
  name: string;
  email: string;
}

export interface Land extends LandInput {
  id: string;
  ownerId: string;
  ownerName: string;
  createdAt: number;
  updatedAt: number;
  /**
   * Set when the owner deleted a land the lab had worked on: the owner no longer
   * sees it (or anything under it), analysts still do and may restore it.
   */
  archivedAt: number | null;
}

/** What `DELETE /api/lands/:id` did: removed everything, or archived the land. */
export type LandRemovalOutcome = "deleted" | "archived";

export interface LandDeletion {
  outcome: LandRemovalOutcome;
}

/**
 * What deleting the land would do for this viewer. Owners archive a land with
 * lab work (a sample past draft, research or advice, drafts included); the
 * counts are then the lab work that is kept, otherwise what is deleted.
 */
export interface LandRemoval {
  outcome: LandRemovalOutcome;
  samples: number;
  research: number;
  advice: number;
}

/** Key figures of the latest completed research of a land. */
export interface ResearchHeadline {
  researchId: string;
  sampleId: string;
  sampleCode: string;
  analyzedAt: number;
  score: number | null;
  grade: Grade | null;
  fbRatio: number | null;
  bacteriaUgPerG: number | null;
  fungiUgPerG: number | null;
}

export interface LandSummary extends Land {
  sampleCount: number;
  /** Samples sent, received or being analysed. */
  openSampleCount: number;
  /** Research visible to the viewer (owners: completed only). */
  researchCount: number;
  /** Advice visible to the viewer (owners: published only). */
  adviceCount: number;
  latest: ResearchHeadline | null;
}

export interface LandDetail {
  land: Land;
  samples: SampleSummary[];
  /** Visible research, newest analyzedAt first. */
  research: ResearchSummary[];
  /** Visible advice, newest first. */
  advice: AdviceSummary[];
  removal: LandRemoval;
}

// ---------------------------------------------------------------------------
// Samples (monsters)
// ---------------------------------------------------------------------------

export const sampleInputSchema = z.object({
  landId: z.string().min(1),
  /** Spot within the land, e.g. "Perceel noord, plek 3". */
  label: z.string().trim().max(120),
  sampledAt: z.number().int().positive(),
  depthCm: z.number().int().min(1).max(200),
  /** Current crop or vegetation. */
  crop: z.string().trim().max(120),
  moisture: z.enum(MOISTURE_STATES),
  notes: z.string().trim().max(4000),
});
export type SampleInput = z.infer<typeof sampleInputSchema>;

/** A sample never moves to another land. */
export const updateSampleInputSchema = sampleInputSchema.omit({ landId: true });
export type UpdateSampleInput = z.infer<typeof updateSampleInputSchema>;

export interface SampleSummary {
  id: string;
  /** Lab reference, e.g. BM-2026-0007; unique, assigned by the server. */
  code: string;
  landId: string;
  landName: string;
  landUse: LandUse;
  ownerId: string;
  ownerName: string;
  label: string;
  sampledAt: number;
  depthCm: number;
  crop: string;
  moisture: MoistureState;
  notes: string;
  status: SampleStatus;
  sentAt: number | null;
  receivedAt: number | null;
  /** Research of this sample when visible to the viewer (owners: completed only). */
  researchId: string | null;
  researchStatus: ResearchStatus | null;
  score: number | null;
  grade: Grade | null;
  /** Newest advice on that research the viewer may see (owners: published only). */
  adviceId: string | null;
  createdAt: number;
  updatedAt: number;
}

// ---------------------------------------------------------------------------
// Research (onderzoeken): the analyst's microscopy input and results
// ---------------------------------------------------------------------------

const count = z.number().int().min(0).max(1_000_000);
const MAX_BACTERIA_FIELDS = 200;
/** A class total may hold every cell of every field, so it is bounded by the field total. */
const classCount = z
  .number()
  .int()
  .min(0)
  .max(MAX_BACTERIA_FIELDS * 1_000_000);
/** Total suspension volume (mL) per gram of sample: 1:10 → 10. */
export const dilutionSchema = z.number().min(1).max(1_000_000);
/** Null until the analyst enters it; there is no default dilution. */
const dilution = dilutionSchema.nullable();
const mpnPerG = z.number().min(0).max(1e9).nullable();

/** Most-probable-number culture results per gram FRESH sample; null = not cultured. */
const protozoaMpnSchema = z.object({
  flagellatesPerG: mpnPerG,
  amoebaePerG: mpnPerG,
  ciliatesPerG: mpnPerG,
});
export type ProtozoaMpn = z.infer<typeof protozoaMpnSchema>;

/** Bumped whenever the stored AnalysisInput shape changes; older rows are upgraded on read. */
export const ANALYSIS_INPUT_VERSION = 3;

/** Organism groups of AnalysisInput, each with its own dilution. */
export const ANALYSIS_GROUPS = ["bacteria", "fungi", "protozoa", "nematodes"] as const;
export type AnalysisGroup = (typeof ANALYSIS_GROUPS)[number];

/** `params.reason` of the custom issue when a bacterial cell is shorter than it is wide. */
export const CELL_LENGTH_BELOW_WIDTH = "cell_length_below_width";
/** `params.reason` when bacterial class totals differ from the field totals. */
export const BACTERIA_CLASS_COUNTS_MISMATCH = "bacteria_class_counts_mismatch";

const hyphaSchema = z.object({
  id: z.string().min(1).max(64),
  kind: z.enum(HYPHA_KINDS),
  color: z.enum(HYPHA_COLORS),
  lengthUm: z.number().positive().max(100_000),
  diameterUm: z.number().positive().max(50),
});
export type Hypha = z.infer<typeof hyphaSchema>;

/**
 * Area (µm²) of one camera frame from the photos' calibration; null = the eyepiece field
 * (circle of fovDiameterUm at the group's magnification).
 */
const imagedArea = z.number().min(100).max(25_000_000).nullable();

/** Provenance of the last "apply accepted objects" of a group. */
const captureRecordSchema = z.object({
  appliedAt: z.number().int().positive(),
  photos: z.number().int().min(0),
  objects: z.number().int().min(0),
  /** model@version of every runner whose objects were applied, sorted, unique. */
  models: z.array(z.string().max(100)).max(50),
});
export type CaptureRecord = z.infer<typeof captureRecordSchema>;

/**
 * Raw direct-microscopy observations. Every group has its own dilution: total
 * suspension volume (mL) per gram of sample, e.g. 1 g made up to 5 mL in total ≈ 5.
 * Field counts are totals over `fieldsExamined` fields (eyepiece fields of view, or camera
 * frames when `imagedAreaUm2` is set); nematodes are counted by scanning whole coverslips
 * (one drop each).
 */
export const analysisInputSchema = z.object({
  version: z.literal(ANALYSIS_INPUT_VERSION),
  /** Water content of the sample (% of fresh weight); null reports per gram fresh sample. */
  moisturePercent: z.number().min(0).max(90).nullable(),
  /** Volume of one drop under the coverslip (µL). */
  dropVolumeUl: z.number().min(1).max(1000),
  /** Side of the square coverslip (mm). */
  coverslipMm: z.number().min(5).max(60),
  /** Field-of-view diameter (µm) per magnification; the minimum catches "1.800" read as 1.8. */
  fovDiameterUm: z.object({
    x100: z.number().min(50).max(5000),
    x400: z.number().min(50).max(2000),
  }),
  bacteria: z
    .object({
      dilution,
      imagedAreaUm2: imagedArea,
      /** Bacteria counted in each 400× field. */
      fieldCounts: z.array(count).max(MAX_BACTERIA_FIELDS),
      classCounts: z.record(z.enum(BACTERIA_CLASSES), classCount),
      /** Cocci have length equal to width. */
      cellLengthUm: z.number().positive().max(10),
      cellWidthUm: z.number().positive().max(5),
    })
    .refine((bacteria) => bacteria.cellLengthUm >= bacteria.cellWidthUm, {
      path: ["cellLengthUm"],
      message: CELL_LENGTH_BELOW_WIDTH,
      params: { reason: CELL_LENGTH_BELOW_WIDTH },
    })
    .refine(
      (bacteria) =>
        BACTERIA_CLASSES.reduce((sum, cls) => sum + bacteria.classCounts[cls], 0) ===
        bacteria.fieldCounts.reduce((sum, cells) => sum + cells, 0),
      {
        path: ["classCounts"],
        message: BACTERIA_CLASS_COUNTS_MISMATCH,
        params: { reason: BACTERIA_CLASS_COUNTS_MISMATCH },
      },
    ),
  fungi: z.object({
    dilution,
    magnification: z.union([z.literal(100), z.literal(400)]),
    imagedAreaUm2: imagedArea,
    fieldsExamined: z.number().int().min(0).max(200),
    hyphae: z.array(hyphaSchema).max(500),
  }),
  protozoa: z.object({
    dilution,
    imagedAreaUm2: imagedArea,
    /** 400× fields examined. */
    fieldsExamined: z.number().int().min(0).max(200),
    flagellates: count,
    nakedAmoebae: count,
    testateAmoebae: count,
    ciliates: count,
    cysts: z.object({ flagellates: count, nakedAmoebae: count, ciliates: count }),
    inactiveTestateAmoebae: count,
    mpn: protozoaMpnSchema,
  }),
  nematodes: z.object({
    dilution,
    /** Whole coverslips (drops) scanned at 100×. */
    dropsExamined: z.number().int().min(0).max(50),
    bacterialFeeders: count,
    fungalFeeders: count,
    predatory: count,
    rootFeeders: count,
    switchers: count,
    bacterialOmnivores: count,
    fungalOmnivores: count,
    inactive: z.object({
      bacterialFeeders: count,
      fungalFeeders: count,
      predatory: count,
      rootFeeders: count,
      switchers: count,
      bacterialOmnivores: count,
      fungalOmnivores: count,
      unresolved: count,
    }),
  }),
  /** Per group the last "apply accepted objects"; null = typed in by hand. */
  capture: z.object({
    bacteria: captureRecordSchema.nullable(),
    fungi: captureRecordSchema.nullable(),
    protozoa: captureRecordSchema.nullable(),
    nematodes: captureRecordSchema.nullable(),
  }),
});
export type AnalysisInput = z.infer<typeof analysisInputSchema>;

/** Stored before bacterial class/state tallies and the nine-colour vocabulary. */
export interface AnalysisInputV2 extends Omit<
  AnalysisInput,
  "version" | "bacteria" | "fungi" | "protozoa" | "nematodes"
> {
  version: 2;
  bacteria: Omit<AnalysisInput["bacteria"], "classCounts">;
  fungi: Omit<AnalysisInput["fungi"], "hyphae"> & {
    hyphae: (Omit<Hypha, "color"> & { color: LegacyHyphaColor })[];
  };
  protozoa: Omit<AnalysisInput["protozoa"], "cysts" | "inactiveTestateAmoebae">;
  nematodes: Omit<
    AnalysisInput["nematodes"],
    "switchers" | "bacterialOmnivores" | "fungalOmnivores" | "inactive"
  >;
}

/** Stored before photos: every field counted through the eyepiece, no capture provenance. */
export interface AnalysisInputV1 extends Omit<
  AnalysisInputV2,
  "version" | "bacteria" | "fungi" | "protozoa" | "capture"
> {
  version: 1;
  bacteria: Omit<AnalysisInputV2["bacteria"], "imagedAreaUm2">;
  fungi: Omit<AnalysisInputV2["fungi"], "imagedAreaUm2">;
  protozoa: Omit<AnalysisInputV2["protozoa"], "imagedAreaUm2">;
}

/** Stored inputs are never rewritten by a migration; readers upgrade them structurally. */
export function upgradeAnalysisInput(
  stored: AnalysisInputV1 | AnalysisInputV2 | AnalysisInput,
): AnalysisInput {
  if (stored.version === ANALYSIS_INPUT_VERSION) return stored;
  const input: AnalysisInputV2 =
    stored.version === 2
      ? stored
      : {
          ...stored,
          version: 2,
          bacteria: { ...stored.bacteria, imagedAreaUm2: null },
          fungi: { ...stored.fungi, imagedAreaUm2: null },
          protozoa: { ...stored.protozoa, imagedAreaUm2: null },
          capture: { bacteria: null, fungi: null, protozoa: null, nematodes: null },
        };
  return {
    ...input,
    version: ANALYSIS_INPUT_VERSION,
    bacteria: {
      ...input.bacteria,
      classCounts: {
        ...emptyBacteriaClassCounts(),
        bacterium: input.bacteria.fieldCounts.reduce((sum, cells) => sum + cells, 0),
      },
    },
    fungi: {
      ...input.fungi,
      hyphae: input.fungi.hyphae.map((hypha) => ({
        ...hypha,
        color: upgradeHyphaColor(hypha.color),
      })),
    },
    protozoa: {
      ...input.protozoa,
      cysts: { flagellates: 0, nakedAmoebae: 0, ciliates: 0 },
      inactiveTestateAmoebae: 0,
    },
    nematodes: {
      ...input.nematodes,
      switchers: 0,
      bacterialOmnivores: 0,
      fungalOmnivores: 0,
      inactive: {
        bacterialFeeders: 0,
        fungalFeeders: 0,
        predatory: 0,
        rootFeeders: 0,
        switchers: 0,
        bacterialOmnivores: 0,
        fungalOmnivores: 0,
        unresolved: 0,
      },
    },
  };
}

export interface Indicator {
  key: IndicatorKey;
  value: number | null;
  unit: IndicatorUnit;
  /** Target range for the land use; null = unbounded on that side. */
  min: number | null;
  max: number | null;
  status: IndicatorStatus;
  /** 0–100; null when missing or unscored. */
  score: number | null;
  source: IndicatorSource;
  /**
   * Smallest non-zero value the counting effort can report, in `unit`; null for ratios,
   * MPN values and unmeasured groups. Shares: 1 / organisms counted in the group.
   */
  detectionLimit: number | null;
  /** Set only when status is "unscored". */
  unscoredReason: UnscoredReason | null;
}

export interface InclusionRules {
  /** Classes in bacteria per gram and bacterial biomass. */
  bacteria: readonly BacteriaClass[];
  /** Bacterial biomass under the F:B ratio. */
  fbBacteria: readonly BacteriaClass[];
  /** Reported per gram and as a share of all counted cells; not scored. */
  diseaseForms: readonly BacteriaClass[];
  /** Kinds in fungal biomass, length, mean diameter, colour share and F:B. */
  fungi: readonly HyphaKind[];
  protozoa: { states: readonly (OrganismState | null)[] };
  nematodes: {
    states: readonly (OrganismState | null)[];
    beneficial: readonly NematodeGroup[];
    rootFeeders: readonly NematodeGroup[];
  };
}

/** Method 1 recorded only unresolved bacteria, no states or new nematode feeding groups. */
const INCLUSION_V1: InclusionRules = {
  bacteria: ["bacterium"],
  fbBacteria: ["bacterium"],
  diseaseForms: [],
  fungi: ["fungal"],
  protozoa: { states: [null] },
  nematodes: {
    states: [null],
    beneficial: ["bacterialFeeders", "fungalFeeders", "predatory"],
    rootFeeders: ["rootFeeders"],
  },
};

/**
 * Every constant behind a score, stored with the results so an old score stays
 * explainable after the method changes (domain/scoring.ts methodFor()).
 */
export interface ScoringMethod {
  version: number;
  landUse: LandUse;
  targets: Targets;
  sources: TargetSources;
  inclusion: InclusionRules;
  /** Re-normalised over the scored indicators. */
  weights: Record<IndicatorKey, number>;
  /** A score at or above the bound earns the grade; below `moderate` is poor. */
  gradeBounds: Record<Exclude<Grade, "poor">, number>;
  /** Below this share of the total weight scored, the score is partial and gets no grade. */
  minCoverage: number;
  /** Any scored indicator below `belowScore` limits the grade to `maxGrade`. */
  gradeCap: { belowScore: number; maxGrade: Grade };
  /** Fields or drops to examine before a group's indicators are scored. */
  minEffort: {
    bacteriaFields: number;
    fungiFields: number;
    protozoaFields: number;
    nematodeDrops: number;
  };
  constants: {
    /** Dry mass per biovolume. */
    dryMassPgPerUm3: { bacteria: number; fungi: number };
    /** Equal densities make the F:B biomass ratio a biovolume ratio. */
    fbRatioBasis: "biovolume";
    /** One hypha of this diameter crossing one field of view sets the fungal detection limit. */
    fungalDetectionDiameterUm: number;
  };
}

/** Bumped whenever the stored AnalysisResults shape changes. */
export const RESULTS_VERSION = 2;

/**
 * Computed from AnalysisInput by domain/analysis.ts; per gram of sample (see basis).
 * Detection limits are one organism (one hypha crossing one field for fungi) over the
 * examined fields or drops; null when the group was not measured.
 */
export interface AnalysisResults {
  version: typeof RESULTS_VERSION;
  /** The method the results were computed with. */
  method: ScoringMethod;
  basis: "fresh" | "dry";
  bacteria: {
    cellsPerG: number | null;
    biomassUgPerG: number | null;
    beneficialBiomassUgPerG: number | null;
    diseaseFormsPerG: number | null;
    diseaseFormShare: number | null;
    detectionLimitCellsPerG: number | null;
    fieldsCounted: number;
  };
  fungi: {
    /** Hypha kinds in method.inclusion.fungi. */
    biomassUgPerG: number | null;
    hyphalLengthMPerG: number | null;
    meanDiameterUm: number | null;
    oomyceteUgPerG: number | null;
    actinomyceteUgPerG: number | null;
    /** Share (0–1) of beneficial hyphal length per colour; null when no fungal hyphae. */
    colorShare: Record<HyphaColor, number> | null;
    detectionLimitUgPerG: number | null;
  };
  /** Included fungal biomass / beneficial bacterial biomass. */
  fbRatio: number | null;
  protozoa: {
    flagellatesPerG: number | null;
    nakedAmoebaePerG: number | null;
    testateAmoebaePerG: number | null;
    /** Naked + testate. */
    amoebaePerG: number | null;
    ciliatesPerG: number | null;
    cystsPerG: number | null;
    inactiveTestateAmoebaePerG: number | null;
    detectionLimitPerG: number | null;
    /** Protozoa counted in method.inclusion.protozoa.states. */
    counted: number;
    /** Ciliates / counted; null when not measured or nothing counted. */
    ciliateShare: number | null;
    /** The analyst's MPN values on the same basis as the counts. */
    mpn: ProtozoaMpn;
  };
  nematodes: {
    bacterialFeedersPerG: number | null;
    fungalFeedersPerG: number | null;
    predatoryPerG: number | null;
    rootFeedersPerG: number | null;
    switchersPerG: number | null;
    bacterialOmnivoresPerG: number | null;
    fungalOmnivoresPerG: number | null;
    inactivePerG: number | null;
    /** The groups in method.inclusion.nematodes.beneficial. */
    beneficialPerG: number | null;
    totalPerG: number | null;
    detectionLimitPerG: number | null;
    /** All feeding groups in method.inclusion.nematodes.states. */
    counted: number;
    /** Root feeders / counted; null when not measured or nothing counted. */
    rootFeederShare: number | null;
  };
  /** One per INDICATOR_KEYS entry, same order. */
  indicators: Indicator[];
  /** Weighted 0–100 soil-life score over the scored indicators; null when none was scored. */
  score: number | null;
  /** Scored weight / total weight, 0–1. */
  coverage: number;
  /** A score over less than method.minCoverage of the weight. */
  partial: boolean;
  /** Null when score is null or partial. */
  grade: Grade | null;
  /** The lowest indicator below method.gradeCap.belowScore, when the cap lowered the grade. */
  gradeCappedBy: IndicatorKey | null;
}

export interface AnalysisResultsV1 extends Omit<
  AnalysisResults,
  "version" | "method" | "bacteria" | "fungi" | "protozoa" | "nematodes"
> {
  version: 1;
  method: Omit<ScoringMethod, "inclusion">;
  bacteria: Omit<
    AnalysisResults["bacteria"],
    "beneficialBiomassUgPerG" | "diseaseFormsPerG" | "diseaseFormShare"
  >;
  fungi: Omit<AnalysisResults["fungi"], "colorShare"> & {
    colorShare: Record<LegacyHyphaColor, number> | null;
  };
  protozoa: Omit<AnalysisResults["protozoa"], "cystsPerG" | "inactiveTestateAmoebaePerG">;
  nematodes: Omit<
    AnalysisResults["nematodes"],
    "switchersPerG" | "bacterialOmnivoresPerG" | "fungalOmnivoresPerG" | "inactivePerG"
  >;
}

/** Upgrade the stored shape, not the historical method or its computed values. */
export function upgradeAnalysisResults(
  stored: AnalysisResultsV1 | AnalysisResults,
): AnalysisResults {
  if (stored.version === RESULTS_VERSION) return stored;
  const colors = stored.fungi.colorShare;
  return {
    ...stored,
    version: RESULTS_VERSION,
    method: { ...stored.method, inclusion: structuredClone(INCLUSION_V1) },
    bacteria: {
      ...stored.bacteria,
      beneficialBiomassUgPerG: stored.bacteria.biomassUgPerG,
      diseaseFormsPerG: null,
      diseaseFormShare: null,
    },
    fungi: {
      ...stored.fungi,
      colorShare:
        colors === null
          ? null
          : {
              clear: colors.colorless,
              yellow: 0,
              orange: 0,
              red: 0,
              green: 0,
              purple: 0,
              tan: colors.amber,
              brown: colors.brown,
              dark_brown: 0,
            },
    },
    protozoa: { ...stored.protozoa, cystsPerG: null, inactiveTestateAmoebaePerG: null },
    nematodes: {
      ...stored.nematodes,
      switchersPerG: null,
      bacterialOmnivoresPerG: null,
      fungalOmnivoresPerG: null,
      inactivePerG: null,
    },
  };
}

export const createResearchInputSchema = z.object({ sampleId: z.string().min(1) });
export type CreateResearchInput = z.infer<typeof createResearchInputSchema>;

/** updatedAt of the row as last read; an older value answers 409 STALE. */
const expectedUpdatedAtSchema = z.number().int().nonnegative();

export const updateResearchInputSchema = z.object({
  analyzedAt: z.number().int().positive(),
  /** Analyst's observations in her own words. */
  summary: z.string().trim().max(8000),
  input: analysisInputSchema,
  status: z.enum(RESEARCH_STATUSES),
  expectedUpdatedAt: expectedUpdatedAtSchema,
});
export type UpdateResearchInput = z.infer<typeof updateResearchInputSchema>;

export const rescoreResearchInputSchema = z.object({ expectedUpdatedAt: expectedUpdatedAtSchema });
export type RescoreResearchInput = z.infer<typeof rescoreResearchInputSchema>;

export interface ResearchSummary {
  id: string;
  sampleId: string;
  sampleCode: string;
  sampleLabel: string;
  sampledAt: number;
  landId: string;
  landName: string;
  /** Land use when the research started; later edits of the land do not change it. */
  landUse: LandUse;
  ownerId: string;
  ownerName: string;
  analystName: string | null;
  status: ResearchStatus;
  analyzedAt: number;
  completedAt: number | null;
  score: number | null;
  grade: Grade | null;
  fbRatio: number | null;
  bacteriaUgPerG: number | null;
  fungiUgPerG: number | null;
  createdAt: number;
  updatedAt: number;
  /** Newest advice on this research the viewer may see (owners: published only). */
  adviceId: string | null;
}

export interface ResearchDetail extends ResearchSummary {
  summary: string;
  input: AnalysisInput;
  results: AnalysisResults;
  /** Reference-set research: analysts annotate its photos independently to measure models. */
  referenceSet: boolean;
}

// ---------------------------------------------------------------------------
// Advice (adviezen)
// ---------------------------------------------------------------------------

export const measureSchema = z.object({
  id: z.string().min(1).max(64),
  category: z.enum(MEASURE_CATEGORIES),
  priority: z.enum(PRIORITIES),
  title: z.string().trim().min(1).max(160),
  description: z.string().trim().max(4000),
  /** Free text, e.g. "Voorjaar 2027" or "Na de oogst". */
  timing: z.string().trim().max(120),
});
export type Measure = z.infer<typeof measureSchema>;

export const createAdviceInputSchema = z.object({
  landId: z.string().min(1),
  /** Completed research of the same land the advice builds on. */
  researchId: z.string().min(1).nullable(),
  title: z.string().trim().min(1).max(160),
});
export type CreateAdviceInput = z.infer<typeof createAdviceInputSchema>;

export const updateAdviceInputSchema = z.object({
  title: z.string().trim().min(1).max(160),
  summary: z.string().trim().max(8000),
  researchId: z.string().min(1).nullable(),
  measures: z.array(measureSchema).max(40),
  /** Recommended date for a follow-up sample. */
  followUpAt: z.number().int().positive().nullable(),
  /** Publishing needs at least one measure. */
  status: z.enum(ADVICE_STATUSES),
  expectedUpdatedAt: expectedUpdatedAtSchema,
});
export type UpdateAdviceInput = z.infer<typeof updateAdviceInputSchema>;

export interface AdviceSummary {
  id: string;
  landId: string;
  landName: string;
  ownerId: string;
  ownerName: string;
  researchId: string | null;
  sampleCode: string | null;
  authorName: string | null;
  status: AdviceStatus;
  title: string;
  measureCount: number;
  highPriorityCount: number;
  followUpAt: number | null;
  publishedAt: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface Advice extends AdviceSummary {
  summary: string;
  measures: Measure[];
  /** The research the advice builds on, when set and visible. */
  research: ResearchSummary | null;
}

/** Output of domain/advice.ts suggestMeasures(). */
export interface Suggestion {
  key: SuggestionKey;
  category: MeasureCategory;
  priority: Priority;
}
